import React, { useState, useEffect, useRef } from 'react';
import cytoscape from 'cytoscape';
import cola from 'cytoscape-cola';
import fcose from 'cytoscape-fcose';

// Register layouts - fcose for blockchain analytics style clustering
cytoscape.use(cola);
cytoscape.use(fcose);

const GraphExplorer = () => {
    const [graphData, setGraphData] = useState(null);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [chainId, setChainId] = useState('mainnet');
    const [limit, setLimit] = useState(1000);
    const [layout, setLayout] = useState('fcose');
    const [nodeFilter, setNodeFilter] = useState('all');
    const [searchAddress, setSearchAddress] = useState('');
    const [selectedNode, setSelectedNode] = useState(null);

    const containerRef = useRef(null);
    const cyRef = useRef(null);

    // Graph API base URL - use same-origin proxy in production
    // Always use /graph-api proxy when not explicitly set via env var
    const GRAPH_API_URL = process.env.REACT_APP_GRAPH_API_URL ||
        (window.location.hostname === 'localhost' && window.location.port === '5173' ? 'http://localhost:3001' : '/graph-api');

    // Fetch graph statistics
    const fetchStats = async () => {
        try {
            const response = await fetch(`${GRAPH_API_URL}/api/graph/stats?chain=${chainId}`, {
                credentials: 'include'
            });

            if (!response.ok) {
                console.error(`Stats fetch failed: HTTP ${response.status}`);
                return;
            }

            const data = await response.json();
            if (data.success) {
                setStats(data.stats);
            }
        } catch (err) {
            console.error('Error fetching stats:', err);
        }
    };

    // Fetch full graph
    const fetchGraph = async () => {
        setLoading(true);
        setError(null);

        try {
            const url = `${GRAPH_API_URL}/api/graph/full?chain=${chainId}&limit=${limit}&node_type=${nodeFilter}`;
            console.log('Fetching graph from:', url);

            // Create abort controller for timeout
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout

            const response = await fetch(url, {
                credentials: 'include',
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            console.log('Response status:', response.status, response.statusText);

            // Check if response is OK before parsing
            if (!response.ok) {
                const errorText = await response.text();
                console.error('Graph API error:', response.status, errorText);
                throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
            }

            const data = await response.json();
            console.log('Graph data received:', {
                success: data.success,
                nodes: data.graph?.nodes?.length || 0,
                edges: data.graph?.edges?.length || 0
            });

            if (data.success) {
                setGraphData(data.graph);
                setStats(data.stats);
            } else {
                setError(data.error || 'Failed to fetch graph');
            }
        } catch (err) {
            console.error('Error fetching graph:', err);
            if (err.name === 'AbortError' || err.name === 'TimeoutError') {
                setError('Request timed out. Try reducing the node limit.');
            } else {
                setError(err.message || 'Failed to fetch graph. Please check the console for details.');
            }
        } finally {
            setLoading(false);
        }
    };

    // Fetch subgraph for specific address
    const fetchSubgraph = async (address) => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(
                `${GRAPH_API_URL}/api/graph/subgraph/${address}?chain=${chainId}`,
                {
                    credentials: 'include'
                }
            );

            // Check if response is OK before parsing
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
            }

            const data = await response.json();

            if (data.success) {
                setGraphData(data.graph);
            } else {
                setError(data.error || 'Failed to fetch subgraph');
            }
        } catch (err) {
            console.error('Error fetching subgraph:', err);
            setError(err.message || 'Failed to fetch subgraph. Please check the console for details.');
        } finally {
            setLoading(false);
        }
    };

    // Unified search for wallets or contracts
    const searchGraphByAddress = async (address) => {
        const normalizedAddress = address.trim().toLowerCase();
        if (!normalizedAddress) {
            setError('Please enter an address to search');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams({
                address: normalizedAddress,
                chain: chainId,
                limit: limit.toString()
            });

            const response = await fetch(
                `${GRAPH_API_URL}/api/graph/search?${params.toString()}`,
                {
                    credentials: 'include'
                }
            );

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
            }

            const data = await response.json();

            if (data.success) {
                setGraphData(data.graph);
                setStats(data.stats || null);
                setSelectedNode(null);
            } else {
                setError(data.error || 'Failed to search graph');
            }
        } catch (err) {
            console.error('Error searching graph:', err);
            setError(err.message || 'Failed to search graph. Please check the console for details.');
        } finally {
            setLoading(false);
        }
    };

    // Initialize graph on mount
    useEffect(() => {
        fetchStats();
    }, [chainId]);

    // Render cytoscape graph
    useEffect(() => {
        if (!graphData || !containerRef.current) return;

        // Clear existing graph
        if (cyRef.current) {
            cyRef.current.destroy();
            cyRef.current = null;
        }

        // Prepare elements
        const elements = [];

        // Add nodes
        (graphData.nodes || []).forEach(node => {
            elements.push({
                data: {
                    id: node.id,
                    label: node.label || node.id.substring(0, 10) + '...',
                    type: node.type || 'wallet',
                    fullAddress: node.id,
                    isSybil: node.is_sybil || false,
                    clusterId: node.cluster_id || node.community,
                    community: node.community,
                    communityNodeCount: node.community_node_count || 0,
                    communityEdgeCount: node.community_edge_count || 0,
                    status: node.status || '',
                    labelText: node.label_text || node.label || '',
                    sybilSubtype: node.sybil_subtype || '',
                    typology: node.typology || '',
                    interactingContracts: node.interacting_contracts || '',
                    nodeContracts: node.node_contracts || ''
                }
            });
        });

        // Add edges
        (graphData.edges || []).forEach(edge => {
            elements.push({
                data: {
                    id: `${edge.source}-${edge.target}`,
                    source: edge.source,
                    target: edge.target,
                    label: edge.label || '',
                    edgeType: edge.edge_type || 'transfer',
                    txCount: edge.tx_count || 0
                }
            });
        });

        // Initialize Cytoscape
        const cy = cytoscape({
            container: containerRef.current,
            elements,
            // Interaction settings for better clicking
            userZoomingEnabled: true,
            userPanningEnabled: true,
            boxSelectionEnabled: false, // Disable box selection to prevent conflicts
            selectionType: 'single',
            autoungrabify: false,
            autounselectify: false,
            minZoom: 0.1,
            maxZoom: 3,
            style: [
                // Wallet nodes
                {
                    selector: 'node[type = "wallet"]',
                    style: {
                        'background-color': '#5a6f8e',
                        'label': 'data(label)',
                        'font-size': 10,
                        'color': '#fff',
                        'text-valign': 'center',
                        'text-halign': 'center',
                        'width': 40,
                        'height': 40,
                        'border-width': 2,
                        'border-color': '#5a6f8e',
                        'border-style': 'dashed',
                        'overlay-padding': 6, // Increase clickable area around nodes
                        'events': 'yes' // Ensure node events are enabled
                    }
                },
                // Sybil wallet nodes
                {
                    selector: 'node[isSybil = true]',
                    style: {
                        'background-color': '#da4b4b',
                        'border-color': '#da4b4b',
                        'border-width': 3,
                        'border-style': 'dashed'
                    }
                },
                // Contract nodes
                {
                    selector: 'node[type = "contract"]',
                    style: {
                        'shape': 'roundrectangle',
                        'background-color': '#ffc107',
                        'label': 'data(label)',
                        'font-size': 10,
                        'color': '#000',
                        'text-valign': 'center',
                        'text-halign': 'center',
                        'width': 50,
                        'height': 50,
                        'border-width': 2,
                        'border-color': '#ff9800'
                    }
                },
                // Transfer edges
                {
                    selector: 'edge[edgeType = "transfer"]',
                    style: {
                        'line-color': '#5a6f8e',
                        'width': 2,
                        'target-arrow-shape': 'triangle',
                        'target-arrow-color': '#5a6f8e',
                        'curve-style': 'bezier',
                        'arrow-scale': 1.0
                    }
                },
                // Contract edges
                {
                    selector: 'edge[edgeType = "contract"]',
                    style: {
                        'line-color': '#20c997',
                        'width': 2,
                        'target-arrow-shape': 'triangle',
                        'target-arrow-color': '#20c997',
                        'curve-style': 'bezier',
                        'arrow-scale': 1.0
                    }
                },
                // Selected node (built-in Cytoscape selection)
                {
                    selector: 'node:selected',
                    style: {
                        'border-width': 5,
                        'border-color': '#0dcaf0',
                        'background-color': '#0d6efd'
                    }
                },
                // Custom selected element (more visible highlight)
                {
                    selector: '.selected-element',
                    style: {
                        'border-width': 6,
                        'border-color': '#00ff00',
                        'z-index': 999
                    }
                },
                // Selected edge
                {
                    selector: 'edge.selected-element',
                    style: {
                        'width': 4,
                        'line-color': '#00ff00',
                        'target-arrow-color': '#00ff00',
                        'z-index': 999
                    }
                }
            ],
            layout: layout === 'fcose' ? {
                // FCOSE: Industry-standard for blockchain analytics (Chainalysis/TRM style)
                name: 'fcose',
                animate: false, // Disable for performance
                fit: true,
                padding: 50,
                quality: 'default',
                randomize: true,
                nodeSeparation: 120,
                idealEdgeLength: 100,
                edgeElasticity: 0.45,
                nestingFactor: 0.1,
                gravity: 0.25,
                gravityRange: 3.8,
                nodeRepulsion: 7500,
                numIter: 3500,
                tile: true,
                packComponents: true
            } : {
                name: layout,
                animate: true,
                animationDuration: 500,
                fit: true,
                padding: 50
            }
        });

        // Add click handler for nodes (priority over edges)
        cy.on('tap', 'node', function (evt) {
            evt.stopPropagation(); // Prevent edge clicks from firing
            const node = evt.target;
            const data = node.data();

            // Highlight selected node
            cy.elements().removeClass('selected-element');
            node.addClass('selected-element');

            setSelectedNode({
                address: data.fullAddress,
                type: data.type,
                isSybil: data.isSybil,
                clusterId: data.clusterId,
                community: data.community,
                communityNodeCount: data.communityNodeCount,
                communityEdgeCount: data.communityEdgeCount,
                status: data.status,
                labelText: data.labelText,
                sybilSubtype: data.sybilSubtype,
                typology: data.typology,
                interactingContracts: data.interactingContracts,
                nodeContracts: data.nodeContracts,
                degree: node.degree(),
                isEdge: false
            });
        });

        // Add click handler for edges (only fires if node wasn't clicked)
        cy.on('tap', 'edge', function (evt) {
            const edge = evt.target;
            const data = edge.data();

            // Highlight selected edge
            cy.elements().removeClass('selected-element');
            edge.addClass('selected-element');

            setSelectedNode({
                isEdge: true,
                source: data.source,
                target: data.target,
                type: data.edgeType,
                txCount: data.txCount
            });
        });

        // Click on background to deselect
        cy.on('tap', function (evt) {
            if (evt.target === cy) {
                cy.elements().removeClass('selected-element');
                setSelectedNode(null);
            }
        });

        // Fit view after layout
        cy.ready(() => {
            cy.fit(undefined, 50);
        });

        cyRef.current = cy;

        // Cleanup
        return () => {
            if (cyRef.current) {
                cyRef.current.destroy();
                cyRef.current = null;
            }
        };
    }, [graphData, layout]);

    // Apply new layout
    const applyLayout = (layoutName) => {
        setLayout(layoutName);
        if (cyRef.current) {
            cyRef.current.layout({ name: layoutName, animate: true, fit: true, padding: 50 }).run();
        }
    };

    // Search for address
    const handleSearch = (e) => {
        e.preventDefault();
        if (searchAddress.trim()) {
            searchGraphByAddress(searchAddress.trim());
        }
    };

    // Reset view
    const handleReset = () => {
        setSearchAddress('');
        setNodeFilter('all');
        fetchGraph();
    };

    // Fit graph to view
    const handleFitView = () => {
        if (cyRef.current) {
            cyRef.current.fit(undefined, 50);
        }
    };

    // Export as image
    const handleExport = () => {
        if (cyRef.current) {
            const png64 = cyRef.current.png({ full: true, scale: 2 });
            const link = document.createElement('a');
            link.href = png64;
            link.download = `graph-${chainId}-${Date.now()}.png`;
            link.click();
        }
    };

    return (
        <div className="container-fluid py-4 px-4">
            <div className="row g-4">
                {/* Left Sidebar - Controls */}
                <div className="col-lg-3">
                    <div className="bg-black border border-secondary rounded p-4 sticky-top" style={{ top: '100px' }}>
                        <h5 className="text-white mb-4">
                            <i className="bi bi-sliders me-2"></i>
                            Controls
                        </h5>

                        {/* Statistics */}
                        {stats && (
                            <div className="mb-4">
                                <h6 className="text-secondary mb-3">Graph Statistics</h6>
                                <div className="small">
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-secondary">Total Nodes:</span>
                                        <span className="text-white fw-bold">{stats.total_nodes?.toLocaleString()}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-secondary">Wallets:</span>
                                        <span className="text-info">{stats.wallets?.toLocaleString()}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-secondary">Contracts:</span>
                                        <span className="text-warning">{stats.contracts?.toLocaleString()}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-secondary">Total Edges:</span>
                                        <span className="text-white fw-bold">{stats.total_edges?.toLocaleString()}</span>
                                    </div>
                                    {stats.sybil_wallets > 0 && (
                                        <>
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-secondary">Sybil Wallets:</span>
                                                <span className="text-danger">{stats.sybil_wallets?.toLocaleString()}</span>
                                            </div>
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-secondary">Clusters:</span>
                                                <span className="text-danger">{stats.clusters}</span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        <hr className="border-secondary" />

                        {/* Search */}
                        <div className="mb-4">
                            <h6 className="text-secondary mb-2">Search Wallet or Contract</h6>
                            <form onSubmit={handleSearch}>
                                <div className="input-group input-group-sm">
                                    <input
                                        type="text"
                                        className="form-control bg-dark text-white border-secondary"
                                        placeholder="Paste any 0x address"
                                        value={searchAddress}
                                        onChange={(e) => setSearchAddress(e.target.value)}
                                    />
                                    <button className="btn btn-primary" type="submit">
                                        <i className="bi bi-search"></i>
                                    </button>
                                </div>
                            </form>
                            <small className="text-muted d-block mt-1" style={{ fontSize: '0.7rem' }}>
                                Wallets show nearby connections. Contracts show all sybil clusters that interacted with them.
                            </small>
                        </div>

                        {/* Chain Selection */}
                        <div className="mb-4">
                            <label className="form-label text-secondary small">Chain</label>
                            <select
                                className="form-select form-select-sm bg-dark text-white border-secondary"
                                value={chainId}
                                onChange={(e) => setChainId(e.target.value)}
                            >
                                <option value="mainnet">Ethereum Mainnet</option>
                                <option value="137">Polygon</option>
                                <option value="42161">Arbitrum</option>
                            </select>
                        </div>

                        {/* Node Limit */}
                        <div className="mb-4">
                            <label className="form-label text-secondary small">Max Nodes</label>
                            <select
                                className="form-select form-select-sm bg-dark text-white border-secondary"
                                value={limit}
                                onChange={(e) => setLimit(parseInt(e.target.value))}
                            >
                                <option value="100">100</option>
                                <option value="500">500</option>
                                <option value="1000">1,000</option>
                                <option value="2000">2,000</option>
                                <option value="5000">5,000</option>
                            </select>
                        </div>

                        {/* Node Filter */}
                        <div className="mb-4">
                            <label className="form-label text-secondary small">Node Type</label>
                            <select
                                className="form-select form-select-sm bg-dark text-white border-secondary"
                                value={nodeFilter}
                                onChange={(e) => setNodeFilter(e.target.value)}
                            >
                                <option value="all">All Nodes</option>
                                <option value="wallet">Wallets Only</option>
                                <option value="contract">Contracts Only</option>
                            </select>
                        </div>

                        {/* Layout Selection */}
                        <div className="mb-4">
                            <label className="form-label text-secondary small">Layout</label>
                            <select
                                className="form-select form-select-sm bg-dark text-white border-secondary"
                                value={layout}
                                onChange={(e) => applyLayout(e.target.value)}
                            >
                                <option value="fcose">Blockchain Analytics (FCOSE)</option>
                                <option value="cose">Force-Directed (COSE)</option>
                                <option value="cola">Cola</option>
                                <option value="circle">Circle</option>
                                <option value="concentric">Concentric</option>
                                <option value="grid">Grid</option>
                                <option value="breadthfirst">Breadth-First</option>
                            </select>
                        </div>

                        <hr className="border-secondary" />

                        {/* Actions */}
                        <div className="d-grid gap-2">
                            <button
                                className="btn btn-primary btn-sm"
                                onClick={fetchGraph}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        Loading...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-arrow-clockwise me-2"></i>
                                        Load Graph
                                    </>
                                )}
                            </button>
                            <button className="btn btn-outline-secondary btn-sm" onClick={handleFitView}>
                                <i className="bi bi-arrows-angle-expand me-2"></i>
                                Fit View
                            </button>
                            <button className="btn btn-outline-secondary btn-sm" onClick={handleExport}>
                                <i className="bi bi-download me-2"></i>
                                Export PNG
                            </button>
                            <button className="btn btn-outline-warning btn-sm" onClick={handleReset}>
                                <i className="bi bi-arrow-counterclockwise me-2"></i>
                                Reset
                            </button>
                        </div>

                        {/* Legend */}
                        <div className="mt-4">
                            <h6 className="text-secondary mb-3">Legend</h6>
                            <div className="small">
                                <div className="d-flex align-items-center mb-2">
                                    <div style={{ width: 20, height: 20, borderRadius: '50%', backgroundColor: '#6c757d', marginRight: 10 }}></div>
                                    <span className="text-white">Wallet</span>
                                </div>
                                <div className="d-flex align-items-center mb-2">
                                    <div style={{ width: 20, height: 20, borderRadius: 3, backgroundColor: '#ffc107', marginRight: 10 }}></div>
                                    <span className="text-white">Contract</span>
                                </div>
                                <div className="d-flex align-items-center mb-2">
                                    <div style={{ width: 20, height: 20, borderRadius: '50%', backgroundColor: '#dc3545', marginRight: 10 }}></div>
                                    <span className="text-white">Sybil</span>
                                </div>
                                <div className="d-flex align-items-center mb-2">
                                    <div style={{ width: 20, height: 2, backgroundColor: '#0d6efd', marginRight: 10 }}></div>
                                    <span className="text-white">Transfer</span>
                                </div>
                                <div className="d-flex align-items-center mb-2">
                                    <div style={{ width: 20, height: 2, backgroundColor: '#20c997', marginRight: 10 }}></div>
                                    <span className="text-white">Contract Call</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Graph Area */}
                <div className="col-lg-9">
                    {/* Selected Node Info */}
                    {selectedNode && (
                        <div className="bg-black border border-secondary rounded p-3 mb-3">
                            {selectedNode.isEdge ? (
                                <div>
                                    <h6 className="text-white mb-2">
                                        <i className="bi bi-arrow-right-circle me-2"></i>
                                        Edge Details
                                    </h6>
                                    <div className="small">
                                        <div><span className="text-secondary">Type:</span> <span className="text-white">{selectedNode.type}</span></div>
                                        <div><span className="text-secondary">From:</span> <code className="text-info">{selectedNode.source.substring(0, 20)}...</code></div>
                                        <div><span className="text-secondary">To:</span> <code className="text-info">{selectedNode.target.substring(0, 20)}...</code></div>
                                        <div><span className="text-secondary">Transactions:</span> <span className="text-white">{selectedNode.txCount}</span></div>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <h6 className="text-white mb-3">
                                        <i className="bi bi-circle-fill me-2" style={{ fontSize: '0.8rem', color: selectedNode.isSybil ? '#dc3545' : selectedNode.type === 'contract' ? '#ffc107' : '#6c757d' }}></i>
                                        Node Details
                                    </h6>
                                    <div className="small">
                                        {/* Address */}
                                        <div className="mb-2">
                                            <span className="text-secondary">Address:</span>
                                            <div><code className="text-info" style={{ fontSize: '0.75rem' }}>{selectedNode.address}</code></div>
                                        </div>

                                        {/* Type */}
                                        <div className="mb-2">
                                            <span className="text-secondary">Type:</span> <span className="text-white text-capitalize">{selectedNode.type}</span>
                                        </div>

                                        {/* Community */}
                                        {selectedNode.community !== undefined && selectedNode.community !== null && (
                                            <div className="mb-2 p-2 bg-primary bg-opacity-10 border border-primary rounded">
                                                <div className="text-primary fw-bold mb-1">
                                                    <i className="bi bi-diagram-2 me-2"></i>
                                                    Community #{selectedNode.community}
                                                </div>
                                                <div className="d-flex gap-3">
                                                    <div>
                                                        <span className="text-secondary">Nodes:</span>
                                                        <span className="text-white ms-1 fw-bold">{selectedNode.communityNodeCount || 0}</span>
                                                    </div>
                                                    <div>
                                                        <span className="text-secondary">Edges:</span>
                                                        <span className="text-white ms-1 fw-bold">{selectedNode.communityEdgeCount || 0}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Status */}
                                        {selectedNode.status && (
                                            <div className="mb-2">
                                                <span className="text-secondary">Status:</span>
                                                <div className={selectedNode.isSybil ? 'text-danger' : 'text-white'}>{selectedNode.status}</div>
                                            </div>
                                        )}

                                        {/* Sybil Information */}
                                        {selectedNode.isSybil && (
                                            <div className="mb-2 p-2 bg-danger bg-opacity-10 border border-danger rounded">
                                                <div className="text-danger fw-bold">⚠️ Sybil Detected</div>
                                                {selectedNode.clusterId && (
                                                    <div className="text-secondary small">Cluster: <span className="text-danger">{selectedNode.clusterId}</span></div>
                                                )}
                                                {selectedNode.sybilSubtype && (
                                                    <div className="text-secondary small">Subtype: <span className="text-danger">{selectedNode.sybilSubtype}</span></div>
                                                )}
                                            </div>
                                        )}

                                        {/* Typology */}
                                        {selectedNode.typology && (
                                            <div className="mb-2">
                                                <span className="text-secondary">Typology:</span>
                                                <div className="text-warning small">{selectedNode.typology}</div>
                                            </div>
                                        )}

                                        {/* Label */}
                                        {selectedNode.labelText && (
                                            <div className="mb-2">
                                                <span className="text-secondary">Label:</span>
                                                <div className="text-white small">{selectedNode.labelText}</div>
                                            </div>
                                        )}

                                        {/* Cluster Contract Interactions */}
                                        {selectedNode.isSybil && selectedNode.interactingContracts && (() => {
                                            try {
                                                const contracts = JSON.parse(selectedNode.interactingContracts);
                                                if (contracts && contracts.length > 0) {
                                                    // Calculate total calls
                                                    const totalCalls = contracts.reduce((sum, item) => sum + (item.count || 1), 0);

                                                    return (
                                                        <div className="mb-2 p-2 bg-info bg-opacity-10 border border-info rounded">
                                                            <div className="text-info fw-bold mb-2">
                                                                <i className="bi bi-diagram-3 me-2"></i>
                                                                Cluster Contract Interactions ({contracts.length})
                                                            </div>
                                                            <div className="small" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                                                {contracts.map((item, idx) => (
                                                                    <div key={idx} className="mb-2 pb-2 border-bottom border-secondary">
                                                                        <div className="mb-1">
                                                                            <span className="text-secondary me-2" style={{ fontSize: '0.65rem' }}>Contract:</span>
                                                                            <code className="text-info" style={{ fontSize: '0.7rem', wordBreak: 'break-all' }}>
                                                                                {item.contract}
                                                                            </code>
                                                                        </div>
                                                                        <div className="d-flex align-items-center">
                                                                            <span className="text-secondary me-2" style={{ fontSize: '0.65rem' }}>Method:</span>
                                                                            <code className="text-warning me-2" style={{ fontSize: '0.7rem' }}>
                                                                                {item.method || '0x'}
                                                                            </code>
                                                                            {item.count && item.count > 1 && (
                                                                                <span className="badge bg-success" style={{ fontSize: '0.6rem' }}>
                                                                                    {item.count}x
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            <div className="text-secondary mt-2" style={{ fontSize: '0.7rem' }}>
                                                                Total: {contracts.length} unique contracts • {totalCalls} total calls
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                            } catch (e) {
                                                console.error('Error parsing interacting_contracts:', e);
                                            }
                                            return null;
                                        })()}

                                        {/* Node Contract Interactions */}
                                        {selectedNode.nodeContracts && (() => {
                                            try {
                                                const contracts = JSON.parse(selectedNode.nodeContracts);
                                                if (contracts && contracts.length > 0) {
                                                    const totalCalls = contracts.reduce((sum, item) => sum + (item.count || 1), 0);

                                                    return (
                                                        <div className="mb-2 p-2 bg-secondary bg-opacity-10 border border-secondary rounded">
                                                            <div className="text-secondary fw-bold mb-2">
                                                                <i className="bi bi-file-earmark-code me-2"></i>
                                                                This Node's Contract Interactions ({contracts.length})
                                                            </div>
                                                            <div className="small" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                                                {contracts.map((item, idx) => (
                                                                    <div key={idx} className="mb-2 pb-2 border-bottom border-secondary">
                                                                        <div className="mb-1">
                                                                            <span className="text-secondary me-2" style={{ fontSize: '0.65rem' }}>Contract:</span>
                                                                            <code className="text-info" style={{ fontSize: '0.7rem', wordBreak: 'break-all' }}>
                                                                                {item.contract}
                                                                            </code>
                                                                        </div>
                                                                        <div className="d-flex align-items-center">
                                                                            <span className="text-secondary me-2" style={{ fontSize: '0.65rem' }}>Method:</span>
                                                                            <code className="text-warning me-2" style={{ fontSize: '0.7rem' }}>
                                                                                {item.method || '0x'}
                                                                            </code>
                                                                            {item.count && item.count > 1 && (
                                                                                <span className="badge bg-primary" style={{ fontSize: '0.6rem' }}>
                                                                                    {item.count}x
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            <div className="text-secondary mt-2" style={{ fontSize: '0.7rem' }}>
                                                                Total: {contracts.length} unique contracts • {totalCalls} total calls
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                            } catch (e) {
                                                console.error('Error parsing node_contracts:', e);
                                            }
                                            return null;
                                        })()}

                                        {/* Connections */}
                                        <div className="mb-2">
                                            <span className="text-secondary">Connections:</span> <span className="text-white">{selectedNode.degree}</span>
                                        </div>
                                    </div>
                                    <button
                                        className="btn btn-sm btn-outline-primary mt-3 w-100"
                                        onClick={() => fetchSubgraph(selectedNode.address)}
                                    >
                                        <i className="bi bi-diagram-3 me-2"></i>
                                        Explore Neighborhood
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Error Display */}
                    {error && (
                        <div className="alert alert-danger" role="alert">
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            {error}
                        </div>
                    )}

                    {/* Graph Container */}
                    <div className="bg-black border border-secondary rounded" style={{ position: 'relative' }}>
                        {!graphData && !loading && (
                            <div className="d-flex align-items-center justify-content-center" style={{ height: '70vh' }}>
                                <div className="text-center">
                                    <i className="bi bi-diagram-3 text-secondary" style={{ fontSize: '4rem' }}></i>
                                    <h4 className="text-secondary mt-3">No Graph Loaded</h4>
                                    <p className="text-secondary">Click "Load Graph" to visualize the network</p>
                                </div>
                            </div>
                        )}

                        {loading && (
                            <div className="d-flex align-items-center justify-content-center" style={{ height: '70vh' }}>
                                <div className="text-center">
                                    <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }}></div>
                                    <p className="text-white mt-3">Loading graph data...</p>
                                </div>
                            </div>
                        )}

                        <div
                            ref={containerRef}
                            style={{
                                height: '70vh',
                                width: '100%',
                                display: graphData && !loading ? 'block' : 'none'
                            }}
                        />
                    </div>

                    {/* Instructions */}
                    <div className="mt-3 small text-secondary">
                        <i className="bi bi-info-circle me-2"></i>
                        <strong>Interact:</strong> Click nodes/edges for details • Drag to pan • Scroll to zoom • Click + drag nodes to rearrange
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GraphExplorer;

